import { useEffect } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Feature, Point, Position } from "geojson";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { ValidationFunction } from "fond/draw/validation";
import { ensureValidationSymbolLayer, removeValidationLayer, setValidationFeatures } from "fond/draw/validation/validationLayers";
import { point as turfPoint } from "fond/turf";

const SOURCE_ID = "validation-endpoint-errors";
const LAYER_ID = "validation-endpoint-errors-symbol";

const ICON_LAYOUT: mapboxgl.SymbolLayout = {
  "text-field": "\uE828",
  "text-padding": 0,
  "text-line-height": -0.45,
  "text-anchor": "bottom",
  "text-rotate": 0.01,
  "text-justify": "center",
  "text-font": ["fontello Regular"],
  "text-size": 20,
};

const ICON_PAINT: mapboxgl.SymbolPaint = {
  "text-color": "#ff0000",
  "text-halo-color": "#ffffff",
  "text-halo-width": 2,
  "text-opacity": 0.5,
};

/**
 * Reads stored invalidEndpoints from feature properties and renders
 * symbol markers at those positions. No re-validation is performed;
 * endpoints are populated during the primary validation pass.
 */
const refreshEndpointErrorMarkers = (map: mapboxgl.Map, drawControl: MapboxDraw): void => {
  ensureValidationSymbolLayer(map, SOURCE_ID, LAYER_ID, ICON_LAYOUT, ICON_PAINT);

  const allFeatures = drawControl.getAll().features as Feature[];
  const markerFeatures: Feature<Point>[] = [];

  for (const feature of allFeatures) {
    const endpointsStr = feature.properties?.[DRAWING_CONSTANTS.InvalidEndpoints];

    if (feature.geometry.type === "LineString" && feature.properties?.[DRAWING_CONSTANTS.Valid] === "false" && endpointsStr) {
      const positions: Position[] = JSON.parse(endpointsStr);
      for (const position of positions) {
        markerFeatures.push(turfPoint(position, { parentFeatureId: feature.id }) as Feature<Point>);
      }
    }
  }

  setValidationFeatures(map, SOURCE_ID, markerFeatures);
};

/**
 * Manages endpoint error marker indicators on the map.
 *
 * Refreshes markers after validation and selection-change events,
 * hides them during drag operations, and removes the layer on unmount.
 */
export const useEndpointErrorMarkers = (
  map: mapboxgl.Map | undefined,
  drawControl: React.MutableRefObject<MapboxDraw | null>,
  validationFunctionsMap: Map<string, ValidationFunction>
): void => {
  useEffect(() => {
    if (!map || !drawControl.current || validationFunctionsMap.size === 0) return undefined;

    let mouseIsDown = false;
    let markersHidden = false;

    const handleRefresh = () => {
      if (!drawControl.current) return;
      markersHidden = false;
      refreshEndpointErrorMarkers(map, drawControl.current);
    };

    const handleMouseDown = () => {
      mouseIsDown = true;
    };

    const handleMouseMove = () => {
      // Clear markers on first mousemove after mousedown (i.e., an actual drag)
      if (mouseIsDown && !markersHidden) {
        markersHidden = true;
        setValidationFeatures(map, SOURCE_ID, []);
      }
    };

    const handleMouseUp = () => {
      if (mouseIsDown) {
        mouseIsDown = false;
        if (markersHidden) {
          // draw.validation fires after drag and will call handleRefresh.
          // Fallback refresh for non-draw drags (e.g. map pan) where no validation fires.
          setTimeout(handleRefresh, 100);
        }
      }
    };

    const handleDelete = (event: MapboxDraw.DrawDeleteEvent) => {
      // Only refresh if a deleted feature was invalid and belongs to a validated layer
      const isRelevant = event.features.some((f) => {
        const layerId = f.properties?.layerId;
        return layerId && validationFunctionsMap.has(layerId) && f.properties?.[DRAWING_CONSTANTS.Valid] === "false";
      });
      if (isRelevant) {
        handleRefresh();
      }
    };

    map.on("draw.validation", handleRefresh);
    map.on("draw.delete", handleDelete);
    // draw.selectionchange fires after undo/redo (drawControl.set() doesn't fire draw.validation)
    map.on("draw.selectionchange", handleRefresh);
    // style.load fires after a full style swap (e.g. satellite → mono) which removes all sources/layers
    map.on("style.load", handleRefresh);
    map.on("mousedown", handleMouseDown);
    map.on("mousemove", handleMouseMove);
    map.on("mouseup", handleMouseUp);

    // Initial refresh for features loaded at startup
    handleRefresh();

    return () => {
      map.off("draw.validation", handleRefresh);
      map.off("draw.delete", handleDelete);
      map.off("draw.selectionchange", handleRefresh);
      map.off("style.load", handleRefresh);
      map.off("mousedown", handleMouseDown);
      map.off("mousemove", handleMouseMove);
      map.off("mouseup", handleMouseUp);
      removeValidationLayer(map, SOURCE_ID, LAYER_ID);
    };
  }, [map, drawControl, validationFunctionsMap]);
};
